import type { NodeGroup, ProjectDocument, ProjectNode } from '../types/project';

// ------------------------------------------------------------
// Node group helpers
// ------------------------------------------------------------

export function nodeGroupLabel(node: ProjectNode): string {
  if (typeof node.group === 'string') return node.group.trim();
  if (
    node.group &&
    typeof node.group === 'object' &&
    typeof (node.group as { name?: string }).name === 'string'
  ) {
    return (node.group as { name: string }).name.trim();
  }
  return '';
}

function parseNodeGroupEntry(entry: unknown): NodeGroup | null {
  if (typeof entry === 'string') {
    const name = entry.trim();
    if (!name) return null;
    return { id: name, name, node_ids: [] };
  }
  if (typeof entry !== 'object' || entry === null) return null;

  const value = entry as Partial<NodeGroup>;
  if (typeof value.id !== 'string' || !value.id.trim()) return null;

  return {
    id: value.id,
    name: value.name?.trim() || value.id,
    node_ids: Array.isArray(value.node_ids) ? [...value.node_ids] : [],
  };
}

export function normalizeNodeGroups(document: ProjectDocument): NodeGroup[] {
  const raw = document.node_groups ?? [];
  const groups = raw
    .map(parseNodeGroupEntry)
    .filter((group): group is NodeGroup => group != null);

  for (const group of groups) {
    if (group.node_ids.length > 0) continue;
    for (const node of document.nodes) {
      const label = nodeGroupLabel(node);
      if (!label) continue;
      if (label === group.name || label === group.id) {
        if (!group.node_ids.includes(node.id)) {
          group.node_ids.push(node.id);
        }
      }
    }
  }

  return groups;
}

export function asNodeGroups(raw: ProjectDocument['node_groups']): NodeGroup[] {
  if (!raw?.length) return [];
  return raw
    .map(parseNodeGroupEntry)
    .filter((group): group is NodeGroup => group != null);
}

export function pruneNodeIdsFromGroups(
  groups: NodeGroup[],
  removedIds: Set<string>,
): NodeGroup[] {
  return groups.map((group) => ({
    ...group,
    node_ids: group.node_ids.filter((id) => !removedIds.has(id)),
  }));
}

export function pruneStaleNodeGroupReferences(
  groups: NodeGroup[],
  validNodeIds: Set<string>,
): NodeGroup[] {
  return groups.map((group) => ({
    ...group,
    node_ids: group.node_ids.filter((id) => validNodeIds.has(id)),
  }));
}

export function syncNodeGroupsToDocument(
  document: ProjectDocument,
  groups: NodeGroup[],
): ProjectDocument {
  const assignment = new Map<string, string>();
  for (const group of groups) {
    const label = group.name || group.id;
    for (const nodeId of group.node_ids) {
      assignment.set(nodeId, label);
    }
  }

  const nodes = document.nodes.map((node) => {
    const assignedLabel = assignment.get(node.id);
    if (assignedLabel != null) {
      return nodeGroupLabel(node) === assignedLabel
        ? node
        : { ...node, group: assignedLabel };
    }

    const currentLabel = nodeGroupLabel(node);
    if (!currentLabel) return node;

    const stillLinked = groups.some(
      (group) =>
        group.node_ids.includes(node.id) ||
        group.name === currentLabel ||
        group.id === currentLabel,
    );
    if (!stillLinked) {
      return { ...node, group: '' };
    }
    return node;
  });

  return { ...document, node_groups: groups, nodes };
}

export function collectGroupMemberIds(
  group: NodeGroup,
  nodes: ProjectNode[],
): Set<string> {
  const memberIds = new Set(
    group.node_ids.filter((id) => nodes.some((node) => node.id === id)),
  );
  for (const node of nodes) {
    const label = nodeGroupLabel(node);
    if (label === group.name || label === group.id) {
      memberIds.add(node.id);
    }
  }
  return memberIds;
}

export function findGroupForNode(
  document: ProjectDocument,
  nodeId: string,
): NodeGroup | null {
  const groups = normalizeNodeGroups(document);
  for (const group of groups) {
    if (collectGroupMemberIds(group, document.nodes).has(nodeId)) {
      return group;
    }
  }
  return null;
}

export function findGroupById(
  document: ProjectDocument,
  groupId: string,
): NodeGroup | null {
  return normalizeNodeGroups(document).find((group) => group.id === groupId) ?? null;
}

export function isNodeInAnyGroup(
  document: ProjectDocument,
  nodeId: string,
): boolean {
  return findGroupForNode(document, nodeId) != null;
}

export function removeNodesFromAllGroups(
  groups: NodeGroup[],
  nodeIds: Set<string>,
): NodeGroup[] {
  return groups.map((group) => ({
    ...group,
    node_ids: group.node_ids.filter((id) => !nodeIds.has(id)),
  }));
}

export function addNodesToGroup(
  groups: NodeGroup[],
  groupId: string,
  nodeIds: string[],
): NodeGroup[] {
  const addSet = new Set(nodeIds);
  return groups.map((group) => {
    if (group.id === groupId) {
      const merged = new Set([...group.node_ids, ...nodeIds]);
      return { ...group, node_ids: [...merged] };
    }
    return {
      ...group,
      node_ids: group.node_ids.filter((id) => !addSet.has(id)),
    };
  });
}

export function staleNodeIdsForGroup(
  group: NodeGroup,
  nodes: ProjectNode[],
): string[] {
  const liveIds = new Set(nodes.map((node) => node.id));
  return group.node_ids.filter((id) => !liveIds.has(id));
}

export function withPrunedNodeGroups(
  document: ProjectDocument,
  removedIds: Set<string>,
): ProjectDocument {
  const groups = normalizeNodeGroups(document);
  if (!groups.length) return document;
  return {
    ...document,
    node_groups: pruneNodeIdsFromGroups(groups, removedIds),
  };
}
